import React, { useEffect, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, CircularProgress, Button, Typography, Stack, Chip, IconButton, Collapse, Paper } from '@mui/material';
import { 
   LocationOn as LocationOnIcon, 
   Event as EventIcon, 
   ExpandMore as ExpandMoreIcon,
   InfoOutlined as InfoIcon,
   ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useTrip } from '../contexts/TripContext';
import TripPhotoPanel from '../components/common/TripPhotoPanel';
import { Accommodation } from '../types/Trip';
import useMap from '../hooks/useMap';
import html2canvas from 'html2canvas';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HotelIcon from '@mui/icons-material/Hotel';
import MapIcon from '@mui/icons-material/Map';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useEvents } from '../contexts/EventContext';
import { get_all_events_and_shared_events, get_all_trips_and_shared_trips } from '../services';
import { MapViewTripPopup } from './MapViewTripAndEventPopup';
import { MapViewEventPopup } from './MapViewTripAndEventPopup';

// Helper function to truncate descriptions
const truncateDescription = (text?: string, maxLength = 50) => {
   if (!text) return '';
   return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Constants
const tripColors = [
   '#FF4081', // Pink
   '#2196F3', // Blue
   '#4CAF50', // Green
   '#FFC107', // Amber
   '#9C27B0', // Purple
   '#3F51B5', // Indigo
   '#8BC34A', // Light Green
   '#FF9800', // Orange
   '#E91E63', // Pink
   '#673AB7', // Deep Purple
   '#009688', // Teal
   '#CDDC39', // Lime
   '#795548', // Brown
];

const defaultCoverImages = [
   'https://images.unsplash.com/photo-1488085061387-422e29b40080?ixlib=rb-4.0.3',
   'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?ixlib=rb-4.0.3',
   'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?ixlib=rb-4.0.3',
   'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?ixlib=rb-4.0.3',
   'https://images.unsplash.com/photo-1504150558240-0b4fd8946624?ixlib=rb-4.0.3',
];

// Types
interface MapViewProps {
   height?: string | number;
}

interface Trip {
   id: string;
   title: string;
   description?: string;
   locations?: string[];
   startDate: Date;
   endDate: Date;
   coverPhotoUrl?: string;
   createdBy: string;
   primaryDestination?: {
      coordinates: [number, number];
      name: string;
   };
}

interface Event {
   id: string;
   title: string;
   description?: string;
   location?: {
      coordinates: [number, number];
      name: string;
   };
   startDate: Date;
   endDate: Date;
   coverPhotoUrl?: string;
   coverImageUrl?: string;
}

const MapView: React.FC<MapViewProps> = ({ height = '100dvh' }) => {
   const [tripPopupOpen, setTripPopupOpen] = useState(false);
   const [eventPopupOpen, setEventPopupOpen] = useState(false);

   const [error, setError] = useState<string | null>(null);
   const [loading, setLoading] = useState(false);
   const [infoExpanded, setInfoExpanded] = useState(false);
   // const [downloading, setDownloading] = useState(false);
   const [selectedTrip, setSelectedTrip] = useState<string | null>(null);
   const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
   const [viewMode, setViewMode] = useState<
      'all' | 'trips' | 'events' | 'photos' | 'accommodations'
   >('all');
   const [events, setEvents] = useState<Event[]>([]);
   const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
   const [photos, setPhotos] = useState<any[]>([]);
   const [availableTrips, setAvailableTrips] = useState<Trip[]>([]);
   const {
      userEvents,
      //   loadEvents
   } = useEvents();

   // Context
   const { currentUser } = useUser();
   // const { trips = [], sharedTrips = [], getTripPhotos } = useTrip();

   // Custom hook for map functionality
   const { mapLoaded, map, mapContainer, resetMap, extendBounds, clearMarkers, createMapMarker } =
      useMap();

   // Memoized values
   // const availableTrips = useMemo(
   //    () => [...trips, ...sharedTrips],
   //    [JSON.stringify({ ...trips }), JSON.stringify({ ...sharedTrips })]
   // );

   const fetchTrips = async () => {
      try {
         if (!currentUser?.uid || !currentUser?.email) {
            console.warn('User not authenticated, skipping trip fetch');
            setAvailableTrips([]);
            return;
         }

         console.log('Fetching trips for user:', currentUser.uid);
         const trips = await get_all_trips_and_shared_trips(currentUser.uid, currentUser.email);
         console.log('Fetched trips:', trips);
         setAvailableTrips(trips as any[]);
      } catch (error) {
         console.error('Error fetching trips:', error);
         setError('Failed to fetch trips');
         setAvailableTrips([]);
      }
   };

   // Fetch events
   const fetchEvents = async () => {
      try {
         if (!currentUser?.uid || !currentUser?.email) {
            console.warn('User not authenticated, skipping events fetch');
            setEvents([]);
            return;
         }

         console.log('Fetching events for user:', currentUser.uid);
         const events = await get_all_events_and_shared_events(currentUser.uid, currentUser.email);
         console.log('Fetched events:', events);
         setEvents(events as any[]);
      } catch (error) {
         console.error('Error fetching events:', error);
         setError('Failed to fetch events');
         setEvents([]);
      }
   };

   const renderOnlyTripAccommodationsOrEventAccommodations = async (
      tripId?: string,
      eventId?: string
   ) => {
      let accommodations: Accommodation[] = [];

      if (tripId) {
         accommodations = (await getAccommodations(tripId, undefined)) as Accommodation[];
      } else if (eventId) {
         accommodations = (await getAccommodations(undefined, eventId)) as Accommodation[];
      }

      clearMarkers();

      loadAccommodationMarkers(accommodations);
   };

   const fetchAccommodations = async (tripId?: string, eventId?: string) => {
      const accommodations = (await getAccommodations(tripId, eventId)) as Accommodation[];
      setAccommodations(accommodations);
   };

   // Fetch accommodations
   const getAccommodations = async (tripId?: string, eventId?: string) => {
      try {
         let accommodationsData: Accommodation[] = [];

         if (tripId) {
            // Get accommodations from specific trip
            const trip = availableTrips.find((t) => t.id === tripId) as any;
            if (trip?.accommodations) {
               accommodationsData = trip.accommodations.map((acc: any) => ({
                  ...acc,
                  tripId: trip.id,
               }));
            }
         } else if (eventId) {
            // Get accommodations from specific event
            const event = events.find((e) => e.id === eventId) as any;
            if (event?.accommodations) {
               accommodationsData = event.accommodations.map((acc: any) => ({
                  ...acc,
                  eventId: event.id,
               }));
            }
         } else {
            // Get all accommodations from all trips and events
            availableTrips.forEach((trip: any) => {
               if (trip?.accommodations) {
                  accommodationsData.push(
                     ...trip?.accommodations.map((acc: any) => ({
                        ...acc,
                        tripId: trip.id,
                     }))
                  );
               }
            });

            events?.forEach((event: any) => {
               if (event?.accommodations) {
                  accommodationsData.push(
                     ...event.accommodations?.map((acc: any) => ({
                        ...acc,
                        eventId: event.id,
                     }))
                  );
               }
            });
         }

         return accommodationsData;
      } catch (error) {
         return [];
      }
   };

   const renderOnlyTripPhotosOrEventPhotos = async (tripId?: string, eventId?: string) => {
      let photos: any[] = [];

      if (tripId) {
         photos = (await getPhotos(tripId, undefined)) as any[];
      } else if (eventId) {
         photos = (await getPhotos(undefined, eventId)) as any[];
      }

      clearMarkers();

      loadPhotoMarkers(photos);
   };

   const fetchPhotos = async (tripId?: string, eventId?: string) => {
      const photos = await getPhotos(tripId, eventId);
      setPhotos(photos || []);
   };
   // Fetch photos
   const getPhotos = async (tripId?: string, eventId?: string) => {
      try {
         let photosQuery;
         if (tripId) {
            photosQuery = query(collection(db, 'photos'), where('tripId', '==', tripId));
         } else if (eventId) {
            photosQuery = query(collection(db, 'photos'), where('eventId', '==', eventId));
         }

         if (photosQuery) {
            const photosSnapshot = await getDocs(photosQuery);
            const photosData = photosSnapshot.docs.map((doc) => ({
               id: doc.id,
               ...doc.data(),
               takenAt: doc.data()?.takenAt?.toDate(),
               photoUrl: doc.data()?.url,
            }));

            return photosData;
         } else {
            return [];
         }
      } catch (error) {
         return [];
      }
   };

   const loadTripMarkers = (trips: Trip[]) => {
      if (!map) return;

      // Group trips by their coordinate as a key
      const groupedTrips = new Map<string, Trip[]>();

      trips.forEach((trip) => {
         const coords = trip?.primaryDestination?.coordinates;
         if (coords) {
            const key = `${coords[0]},${coords[1]}`;
            if (!groupedTrips.has(key)) {
               groupedTrips.set(key, []);
            }
            groupedTrips.get(key)!.push(trip);
         }
      });

      groupedTrips.forEach((group, key) => {
         const [lng, lat] = key.split(',').map(Number);

         // Validate coordinates - latitude must be between -90 and 90, longitude between -180 and 180
         if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn(
               `Invalid coordinates detected for trip(s):`,
               group.map((t) => t.title).join(', ')
            );
            return; // Skip this marker
         }

         // Create marker element
         const el = document.createElement('div');
         el.className = 'trip-marker';
         el.style.width = '40px';
         el.style.height = '40px';
         el.style.position = 'relative';
         el.style.display = 'flex';
         el.style.justifyContent = 'center';
         el.style.alignItems = 'center';
         el.style.cursor = 'pointer';

         // Icon inside marker
         const iconEl = document.createElement('div');
         iconEl.style.width = '32px';
         iconEl.style.height = '32px';
         iconEl.style.display = 'flex';
         iconEl.style.justifyContent = 'center';
         iconEl.style.alignItems = 'center';
         iconEl.style.backgroundColor = '#2196F3';
         iconEl.style.borderRadius = '50%';
         iconEl.style.border = '2px solid white';
         iconEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         iconEl.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="white">
       <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>`;
         el.appendChild(iconEl);

         // Add label for count
         const label = document.createElement('div');
         label.style.position = 'absolute';
         label.style.bottom = '-25px';
         label.style.left = '50%';
         label.style.transform = 'translateX(-50%)';
         label.style.backgroundColor = 'white';
         label.style.color = '#2196f3';
         label.style.padding = '2px 8px';
         label.style.borderRadius = '10px';
         label.style.fontSize = '12px';
         label.style.fontWeight = 'bold';
         label.style.whiteSpace = 'nowrap';
         label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         label.textContent = `${group.length} Trip${group.length > 1 ? 's' : ''}`;
         el.appendChild(label);

         // Create marker and popup
         const marker = createMapMarker({
            element: el,
            anchor: 'bottom',
         });

         const popupHTML = `
      <div style="max-width: 1000px; padding: 8px;min-width: 600px;">
        <div style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center; margin-bottom: 4px; padding: 4px 8px; background: #2196F320; border-radius: 4px;">
            <span style="color: #2196F3; font-weight: bold; text-transform: capitalize; margin-right: 8px;">Trips</span>
            <span style="background: #2196F3; color: white; padding: 2px 6px; border-radius: 12px; font-size: 12px;">${group.length}</span>
          </div>
          <div style="display: flex; overflow-x: auto; gap: 8px; padding: 4px; scrollbar-width: thin;">
            ${group.map((trip) => createGridItem(trip, 'trip')).join('')}
          </div>
        </div>
      </div>
    `;

         marker
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup({ offset: 25, maxWidth: '1000px' }).setHTML(popupHTML))
            .addTo(map);

         extendBounds([lng, lat]);
      });
   };

   const loadEventMarkers = (events: Event[]) => {
      if (!map) return;

      const eventGroups = new Map<string, Event[]>();

      // Group events by coordinates
      events.forEach((event) => {
         if (event?.location?.coordinates) {
            const key = event.location.coordinates.join(',');
            if (!eventGroups.has(key)) eventGroups.set(key, []);
            eventGroups.get(key)!.push(event);
         }
      });

      eventGroups.forEach((groupedEvents, coordKey) => {
         const [lng, lat] = coordKey.split(',').map(Number);

         // Validate coordinates - latitude must be between -90 and 90, longitude between -180 and 180
         if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn(
               `Invalid coordinates detected for event(s):`,
               groupedEvents.map((e) => e.title).join(', ')
            );
            return; // Skip this marker
         }

         const el = document.createElement('div');
         el.className = 'event-marker';
         el.style.width = '40px';
         el.style.height = '40px';
         el.style.position = 'relative';
         el.style.display = 'flex';
         el.style.justifyContent = 'center';
         el.style.alignItems = 'center';
         el.style.cursor = 'pointer';

         const iconEl = document.createElement('div');
         iconEl.style.width = '32px';
         iconEl.style.height = '32px';
         iconEl.style.display = 'flex';
         iconEl.style.justifyContent = 'center';
         iconEl.style.alignItems = 'center';
         iconEl.style.backgroundColor = '#9c27b0';
         iconEl.style.borderRadius = '50%';
         iconEl.style.border = '2px solid white';
         iconEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         iconEl.innerHTML = `
      <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
        <path d="M17 12c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm1.65 7.35L16.5 17.2V14h1v2.79l1.85 1.85-.7.71zM18 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H6c-1.1 0-2 .9-2 2v15c0 1.1.9 2 2 2h6.11c-.59-.57-1.07-1.25-1.42-2H6V5h2v3h8V5h2v5.08c.71.1 1.38.31 2 .6V5c0-1.1-.9-2-2-2zm-6 2c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
      </svg>`;

         el.appendChild(iconEl);

         // Add label showing count
         const label = document.createElement('div');
         label.style.position = 'absolute';
         label.style.bottom = '-25px';
         label.style.left = '50%';
         label.style.transform = 'translateX(-50%)';
         label.style.backgroundColor = 'white';
         label.style.color = '#9c27b0';
         label.style.padding = '2px 8px';
         label.style.borderRadius = '10px';
         label.style.fontSize = '12px';
         label.style.fontWeight = 'bold';
         label.style.whiteSpace = 'nowrap';
         label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         label.textContent = `${groupedEvents.length} Event${groupedEvents.length > 1 ? 's' : ''}`;
         el.appendChild(label);

         const marker = createMapMarker({
            element: el,
            anchor: 'bottom',
         });

         // Build popup HTML
         const popupHTML = `
      <div style="max-width: 1000px; padding: 8px;min-width: 600px;">
        <div style="margin-bottom: 8px;">
          <div style="display: flex; align-items: center; margin-bottom: 4px; padding: 4px 8px; background: #9c27b020; border-radius: 4px;">
            <span style="color: #9c27b0; font-weight: bold; text-transform: capitalize; margin-right: 8px;">Events</span>
            <span style="background: #9c27b0; color: white; padding: 2px 6px; border-radius: 12px; font-size: 12px;">${groupedEvents.length}</span>
          </div>
          <div style="display: flex; overflow-x: auto; gap: 8px; padding: 4px; scrollbar-width: thin;">
            ${groupedEvents.map((event) => createGridItem(event, 'event')).join('')}
          </div>
        </div>
      </div>
    `;

         marker
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup({ offset: 25, maxWidth: '1000px' }).setHTML(popupHTML))
            .addTo(map);

         extendBounds([lng, lat]);
      });
   };

   const loadAccommodationMarkers = (accommodations: Accommodation[]) => {
      if (!map) return;

      // 1. Group accommodations by coordinate string
      const grouped = accommodations.reduce(
         (acc, item) => {
            const key = item.coordinates?.join(','); // e.g., "12.34,56.78"
            if (!key) return acc;
            acc[key] = acc[key] || [];
            acc[key].push(item);
            return acc;
         },
         {} as Record<string, Accommodation[]>
      );

      Object.entries(grouped).forEach(([coordString, accGroup]) => {
         const [lng, lat] = coordString.split(',').map(Number);

         // Validate coordinates - latitude must be between -90 and 90, longitude between -180 and 180
         if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn(
               `Invalid coordinates detected for accommodation(s):`,
               accGroup.map((a) => a.name || 'Unnamed').join(', ')
            );
            return; // Skip this marker
         }

         // 2. Create marker element
         const el = document.createElement('div');
         el.className = 'accommodation-marker';
         el.style.width = '40px';
         el.style.height = '40px';
         el.style.position = 'relative';
         el.style.display = 'flex';
         el.style.justifyContent = 'center';
         el.style.alignItems = 'center';
         el.style.cursor = 'pointer';

         // 3. Create icon circle
         const iconEl = document.createElement('div');
         iconEl.style.width = '32px';
         iconEl.style.height = '32px';
         iconEl.style.display = 'flex';
         iconEl.style.justifyContent = 'center';
         iconEl.style.alignItems = 'center';
         iconEl.style.backgroundColor = '#4caf50';
         iconEl.style.borderRadius = '50%';
         iconEl.style.border = '2px solid white';
         iconEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         iconEl.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="white">
         <path d="M19 7H11V14H3V5H1V20H3V17H21V20H23V11A4 4 0 0,0 19,7M7,13A3,3 0 0,0 10,10A3,3 0 0,0 7,7A3,3 0 0,0 4,10A3,3 0 0,0 7,13Z"/>
      </svg>`;
         el.appendChild(iconEl);

         // 4. Add label with count
         const label = document.createElement('div');
         label.style.position = 'absolute';
         label.style.bottom = '-25px';
         label.style.left = '50%';
         label.style.transform = 'translateX(-50%)';
         label.style.backgroundColor = 'white';
         label.style.color = '#2196f3';
         label.style.padding = '2px 8px';
         label.style.borderRadius = '10px';
         label.style.fontSize = '12px';
         label.style.fontWeight = 'bold';
         label.style.whiteSpace = 'nowrap';
         label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         label.textContent = `${accGroup.length} Accommodation${accGroup.length > 1 ? 's' : ''}`;
         el.appendChild(label);

         // 5. Create popup HTML with all accommodations
         const popupHTML = `
         <div style="max-width: 1000px; padding: 8px;min-width: 600px;">
            <div style="margin-bottom: 8px;">
               <div style="display: flex; align-items: center; margin-bottom: 4px; padding: 4px 8px; background: #4caf5020; border-radius: 4px;">
                  <span style="color: #4caf50; font-weight: bold; text-transform: capitalize; margin-right: 8px;">Accommodations</span>
                  <span style="background: #4caf50; color: white; padding: 2px 6px; border-radius: 12px; font-size: 12px;">${accGroup.length}</span>
               </div>
               <div style="display: flex; overflow-x: auto; gap: 8px; padding: 4px; scrollbar-width: thin;">
                  ${accGroup.map((a) => createGridItem(a, 'accommodation')).join('')}
               </div>
            </div>
         </div>
      `;

         const marker = createMapMarker({
            element: el,
            anchor: 'bottom',
         });

         marker
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup({ offset: 25, maxWidth: '1000px' }).setHTML(popupHTML))
            .addTo(map);

         extendBounds([lng, lat]);
      });
   };

   const loadPhotoMarkers = (photos: any[]) => {
      if (!map) return;

      const photoGroups = new Map<string, any[]>();

      // Group photos by coordinates
      photos.forEach((photo) => {
         if (photo?.location?.coordinates?.longitude && photo?.location?.coordinates?.latitude) {
            const key = `${photo?.location?.coordinates?.longitude},${photo?.location?.coordinates?.latitude}`;
            if (!photoGroups.has(key)) photoGroups.set(key, []);
            photoGroups.get(key)!.push(photo);
         }
      });

      // Convert Map to plain object and use Object.entries
      const groupedObj = Object.fromEntries(photoGroups);

      Object.entries(groupedObj).forEach(([key, groupPhotos]) => {
         const [lng, lat] = key.split(',').map(Number);
         const el = document.createElement('div');
         el.className = 'photo-marker';
         el.style.width = '40px';
         el.style.height = '40px';
         el.style.position = 'relative';
         el.style.display = 'flex';
         el.style.justifyContent = 'center';
         el.style.alignItems = 'center';
         el.style.cursor = 'pointer';

         // Create icon
         const iconEl = document.createElement('div');
         iconEl.style.width = '32px';
         iconEl.style.height = '32px';
         iconEl.style.display = 'flex';
         iconEl.style.justifyContent = 'center';
         iconEl.style.alignItems = 'center';
         iconEl.style.backgroundColor = '#FF9800';
         iconEl.style.borderRadius = '50%';
         iconEl.style.border = '2px solid white';
         iconEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

         iconEl.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="white">
         <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
      </svg>`;

         el.appendChild(iconEl);

         // Add label below marker
         const label = document.createElement('div');
         label.style.position = 'absolute';
         label.style.bottom = '-25px';
         label.style.left = '50%';
         label.style.transform = 'translateX(-50%)';
         label.style.backgroundColor = 'white';
         label.style.color = '#FF9800';
         label.style.padding = '2px 8px';
         label.style.borderRadius = '10px';
         label.style.fontSize = '12px';
         label.style.fontWeight = 'bold';
         label.style.whiteSpace = 'nowrap';
         label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
         label.textContent = `${groupPhotos.length} Photo${groupPhotos.length > 1 ? 's' : ''}`;

         el.appendChild(label);

         const marker = createMapMarker({
            element: el,
            anchor: 'bottom',
         });

         const popupHTML = `
         <div style="max-width: 1000px; padding: 8px;min-width: 600px;">
            <div style="margin-bottom: 8px;">
               <div style="display: flex; align-items: center; margin-bottom: 4px; padding: 4px 8px; background: #FF980020; border-radius: 4px;">
                  <span style="color: #FF9800; font-weight: bold; text-transform: capitalize; margin-right: 8px;">Photos</span>
                  <span style="background: #FF9800; color: white; padding: 2px 6px; border-radius: 12px; font-size: 12px;">${groupPhotos.length}</span>
               </div>
               <div style="display: flex; overflow-x: auto; gap: 8px; padding: 4px; scrollbar-width: thin;">
                  ${groupPhotos.map((photo) => createGridItem(photo, 'photo')).join('')}
               </div>
            </div>
         </div>
      `;

         marker
            .setLngLat([lng, lat])
            .setPopup(new mapboxgl.Popup({ offset: 25, maxWidth: '1000px' }).setHTML(popupHTML))
            .addTo(map);

         extendBounds([lng, lat]);
      });
   };

   const renderOnlyTrips = () => {
      clearMarkers();
      loadTripMarkers(availableTrips);
   };

   const renderOnlyEvents = () => {
      clearMarkers();
      loadEventMarkers(events);
   };

   const loadMarkers = () => {
      if (!map) return;
      clearMarkers();
      loadTripMarkers(availableTrips);
      loadEventMarkers(events);
      // loadAccommodationMarkers(accommodations);
      // loadPhotoMarkers(photos);
   };

   const handleViewModeChange = () => {
      if (viewMode == 'photos' && selectedTrip) {
         renderOnlyTripPhotosOrEventPhotos(selectedTrip, undefined);
         return;
      }

      if (viewMode == 'accommodations' && selectedTrip) {
         renderOnlyTripAccommodationsOrEventAccommodations(selectedTrip, undefined);
         return;
      }

      if (viewMode == 'accommodations' && selectedEvent) {
         renderOnlyTripAccommodationsOrEventAccommodations(undefined, selectedEvent);
         return;
      }

      if (viewMode == 'photos' && selectedEvent) {
         renderOnlyTripPhotosOrEventPhotos(undefined, selectedEvent);
         return;
      }

      if (viewMode == 'trips') {
         renderOnlyTrips();
         return;
      }

      if (viewMode == 'events') {
         renderOnlyEvents();
         return;
      }

      if (viewMode == 'all') {
         loadMarkers();
      }
   };

   const addTripToMarker = (trip: Trip) => {
      try {
         clearMarkers();
         loadTripMarkers([...availableTrips, trip]);
         loadEventMarkers(events);
      } catch (error) {}
   };

   const addEventToMarker = (event: Event) => {
      try {
         clearMarkers();
         loadEventMarkers([...events, event]);
         loadTripMarkers(availableTrips);
      } catch (error) {}
   };

   const handleOpenTripPopup = () => {
      setTripPopupOpen(true);
   };

   const handleOpenEventPopup = () => {
      setEventPopupOpen(true);
   };

   // Load markers based on current view mode and selection
   useEffect(() => {
      loadMarkers();
   }, [
      JSON.stringify({ ...availableTrips }),
      JSON.stringify({ ...events }),
      map,
      mapContainer.current,
      JSON.stringify({ ...accommodations }),
      JSON.stringify({ ...photos }),
   ]);

   useEffect(() => {
      loadMarkers();
   }, []);

   // Load all data when user is authenticated
   useEffect(() => {
      if (currentUser?.uid && currentUser?.email) {
         console.log('User authenticated, fetching map data...');
         fetchEvents();
         fetchTrips();
         fetchAccommodations();
         fetchPhotos();
      } else {
         console.log('User not authenticated, clearing map data...');
         setEvents([]);
         setAvailableTrips([]);
         setAccommodations([]);
         setPhotos([]);
      }
   }, [currentUser?.uid, currentUser?.email]);

   useEffect(() => {
      handleViewModeChange();
   }, [selectedTrip, selectedEvent, viewMode]);

   // Event handlers
   useEffect(() => {
      const handleSelectTrip = (event: CustomEvent) => {
         const tripId = event.detail;
         const trip = availableTrips.find((t) => t.id === tripId);
         setSelectedTrip(tripId || null);
         setSelectedEvent(null);
         setViewMode('all');
      };

      const handleSelectEvent = (event: CustomEvent) => {
         const eventId = event.detail;
         const selectedEvent = events.find((ev) => ev.id === eventId);
         setSelectedEvent(eventId || null);
         setSelectedTrip(null);
         setViewMode('all');
      };

      const handleViewPhotos = (event: CustomEvent) => {
         const id = event.detail;
         const trip = availableTrips.find((t) => t.id === id);
         const eventItem = events.find((e) => e.id === id);

         if (trip) {
            setSelectedTrip(id);
            setSelectedEvent(null);
            setViewMode('photos');
         } else if (eventItem) {
            setSelectedEvent(id);
            setSelectedTrip(null);
            setViewMode('photos');
         }
      };

      const handleViewAccommodations = (event: CustomEvent) => {
         const id = event.detail;
         const trip = availableTrips.find((t) => t.id === id);
         const eventItem = events.find((e) => e.id === id);

         if (trip) {
            setSelectedTrip(id);
            setSelectedEvent(null);
            setViewMode('accommodations');
         } else if (eventItem) {
            setSelectedEvent(id);
            setSelectedTrip(null);
            setViewMode('accommodations');
         }
      };

      window.addEventListener('selectTrip', handleSelectTrip as EventListener);
      window.addEventListener('selectEvent', handleSelectEvent as EventListener);
      // window.addEventListener('viewTripPhotos', handleViewTripPhotos as EventListener);
      window.addEventListener('viewPhotos', handleViewPhotos as EventListener);
      window.addEventListener('viewAccommodations', handleViewAccommodations as EventListener);
      window.addEventListener('openTripPopup', handleOpenTripPopup as EventListener);
      window.addEventListener('openEventPopup', handleOpenEventPopup as EventListener);

      return () => {
         window.removeEventListener('selectTrip', handleSelectTrip as EventListener);
         window.removeEventListener('selectEvent', handleSelectEvent as EventListener);
         window.removeEventListener('openTripPopup', handleOpenTripPopup as EventListener);
         window.removeEventListener('openEventPopup', handleOpenEventPopup as EventListener);
         // window.removeEventListener('viewTripPhotos', handleViewTripPhotos as EventListener);
         window.removeEventListener('viewPhotos', handleViewPhotos as EventListener);
         window.removeEventListener(
            'viewAccommodations',
            handleViewAccommodations as EventListener
         );
      };
   }, [availableTrips, events]);

   return (
      <Box sx={{ position: 'relative', height: height, width: '100%' }}>
         {/* Introduction section - expandable */}
         <Box
            sx={{
               position: 'absolute',
               top: 16,
               left: 16,
               zIndex: 1,
               maxWidth: '500px',
            }}
         >
            {/* Collapsed state - small info button */}
            <IconButton
               onClick={() => setInfoExpanded(!infoExpanded)}
               sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: 2,
                  mb: 1,
                  display: infoExpanded ? 'none' : 'flex',
                  '&:hover': {
                     backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
               }}
               color='primary'
               size='large'
            >
               <InfoIcon />
            </IconButton>

            <MapViewTripPopup
               addTripToView={addTripToMarker}
               open={tripPopupOpen}
               onClose={() => setTripPopupOpen(false)}
            />
            <MapViewEventPopup
               addEventToView={addEventToMarker}
               open={eventPopupOpen}
               onClose={() => setEventPopupOpen(false)}
            />

            {/* Expanded information panel */}
            <Collapse in={infoExpanded}>
               <Paper
                  sx={{
                     backgroundColor: 'rgba(255, 255, 255, 0.9)',
                     borderRadius: 2,
                     p: 2,
                     boxShadow: 2,
                     backdropFilter: 'blur(5px)',
                     position: 'relative',
                  }}
               >
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                     }}
                  >
                     <Typography variant='h6' color='primary'>
                        Your Travel Map
                     </Typography>
                     <IconButton
                        size='small'
                        onClick={() => setInfoExpanded(false)}
                        sx={{
                           p: 0.5,
                           '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                           },
                        }}
                     >
                        <ChevronRightIcon />
                     </IconButton>
                  </Box>
                  <Typography variant='body2' paragraph>
                     This interactive map displays all your trips, events, and photos with location
                     data in one place.
                  </Typography>
                  <Typography variant='body2' paragraph>
                     <strong>• Trips and Events:</strong> Each marker shows a location you've
                     visited or plan to visit. Click on markers to see details and photos.
                  </Typography>
                  <Typography variant='body2' paragraph>
                     <strong>• Photo Locations:</strong> Photos with GPS data extracted from EXIF
                     metadata appear as photo markers.
                  </Typography>
                  <Typography variant='body2'>
                     <strong>• Add More Destinations:</strong> Create new trips or events to expand
                     your travel map. Photos you upload with location data will automatically appear
                     here.
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                     <Button
                        variant='contained'
                        color='primary'
                        size='small'
                        onClick={() => window.dispatchEvent(new CustomEvent('openTripPopup'))}
                     >
                        Add Trip
                     </Button>
                     <Button
                        variant='outlined'
                        color='secondary'
                        size='small'
                        onClick={() => window.dispatchEvent(new CustomEvent('openEventPopup'))}
                     >
                        Add Event
                     </Button>
                  </Box>
               </Paper>
            </Collapse>
         </Box>

         {loading && (
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  backgroundColor: 'rgba(255,255,255,0.5)',
               }}
            >
               <CircularProgress />
            </Box>
         )}

         {error && (
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  padding: 2,
               }}
            >
               <Typography color='error' align='center'>
                  {error}
               </Typography>
            </Box>
         )}

         <Box
            sx={{
               position: 'absolute',
               top: 16,
               right: 16,
               zIndex: 1,
               display: 'flex',
               gap: 1,
               flexDirection: 'column',
            }}
         >
            <Stack direction='row' spacing={1}>
               <Chip
                  icon={<EventIcon />}
                  label='Reset'
                  color={viewMode === 'all' ? 'success' : 'secondary'}
                  onClick={() => {
                     setSelectedTrip(null);
                     setSelectedEvent(null);
                     setViewMode('all');
                  }}
               />
               <Chip icon={<MapIcon />} label='Download' color={'default'} onClick={() => {}} />
            </Stack>

            {!selectedEvent && !selectedTrip && (
               <Stack direction='row' spacing={1}>
                  <Chip
                     icon={<LocationOnIcon />}
                     label='Trips'
                     color={viewMode === 'trips' ? 'success' : 'secondary'}
                     onClick={() => {
                        setViewMode('trips');
                        setSelectedTrip(null);
                        setSelectedEvent(null);
                     }}
                  />
                  <Chip
                     icon={<EventIcon />}
                     label='Events'
                     color={viewMode === 'events' ? 'success' : 'secondary'}
                     onClick={() => {
                        setViewMode('events');
                        setSelectedTrip(null);
                        setSelectedEvent(null);
                     }}
                  />
               </Stack>
            )}

            {(selectedTrip || selectedEvent) && (
               <Stack direction='row' spacing={1}>
                  <Chip
                     icon={<CameraAltIcon />}
                     label='Photos'
                     color={viewMode === 'photos' ? 'success' : 'secondary'}
                     onClick={() => setViewMode('photos')}
                  />
                  <Chip
                     icon={<HotelIcon />}
                     label='Accommodations'
                     color={viewMode === 'accommodations' ? 'success' : 'secondary'}
                     onClick={() => setViewMode('accommodations')}
                  />
               </Stack>
            )}
         </Box>

         <Box
            ref={mapContainer}
            sx={{
               height: '100%',
               width: '100%',
            }}
         />
      </Box>
   );
};

// Helper function to create a grid item
const createGridItem = (item: any, type: string) => {
   const color = {
      trip: '#2196F3',
      event: '#9c27b0',
      accommodation: '#4caf50',
      photo: '#FF9800',
   }[type];

   const label = {
      trip: 'Trip',
      event: 'Event',
      accommodation: 'Accommodation',
      photo: 'Photo',
   }[type];

   return `
      <div style="
         min-width: 200px;
         background: white;
         border-radius: 8px;
         overflow: hidden;
         box-shadow: 0 2px 4px rgba(0,0,0,0.1);
         border: 1px solid ${color};
         position: relative;
      ">
         <div style="
            position: absolute;
            top: 4px;
            right: 4px;
            background: ${color};
            color: white;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: bold;
            z-index: 1;
         ">
            ${label}
         </div>
         <div style="padding: 8px;">
            <h3 style="margin: 0 0 4px 0; font-size: 14px; color: ${color};">
               ${item.title || item.name || 'Untitled'}
            </h3>
         </div>
         ${
            item.coverPhotoUrl || item.photoUrl
               ? `
            <img src="${item.coverPhotoUrl || item.photoUrl}" 
               style="width: 100%; height: 100px; object-fit: cover;"/>
         `
               : ''
         }
         <div style="padding: 8px;">
            ${
               type === 'trip' && item.primaryDestination?.name
                  ? `
               <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                  📍 ${item.primaryDestination.name}
               </p>
            `
                  : ''
            }
            ${
               type === 'event' && item.location?.name
                  ? `
               <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                  📍 ${item.location.name}
               </p>
            `
                  : ''
            }
            ${
               type === 'accommodation' && item.address
                  ? `
               <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                  📍 ${item.address}
               </p>
            `
                  : ''
            }
            ${
               type === 'photo' && item.location?.name
                  ? `
               <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">
                  📍 ${item.location.name}
               </p>
            `
                  : ''
            }
            ${
               type === 'trip' || type === 'event'
                  ? `
               <div style="display: flex; gap: 8px; margin-top: 8px;">
                  <button 
                     onclick="window.dispatchEvent(new CustomEvent('viewPhotos', { detail: '${item.id}' }))"
                     style="
                        background: ${color};
                        color: white;
                        border: none;
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        cursor: pointer;
                     "
                  >
                     View Photos
                  </button>
                  <button 
                     onclick="window.dispatchEvent(new CustomEvent('viewAccommodations', { detail: '${item.id}' }))"
                     style="
                        background: ${color};
                        color: white;
                        border: none;
                        padding: 4px 8px;
                        border-radius: 4px;
                        font-size: 12px;
                        cursor: pointer;
                     "
                  >
                     View Accommodations
                  </button>
               </div>
            `
                  : ''
            }
         </div>
      </div>
   `;
};

export default MapView;
